<template>
  <component
    :is="componentToShow"
    class="z-10 block"
    :href="href"
    :download="computedHrefType === 'download' ? '' : undefined"
    :target="href && href.toLowerCase().startsWith('http') ? '_blank' : ''">
    <component
      :is="`ButtonBase${buttonType}`"
      :href="href"
      :label="label"
      v-bind="type !== 'gradient' ? { theme } : {}"
      :disabled="disabled"
      :href-type="computedHrefType"
      :size="size">
      <slot />
    </component>
  </component>
</template>

<script setup lang="ts">
  /**
   * Base Button component
   *
   * This component is used to render the different button types. It is meant to be used for the
   * button types: filled, gradient, outline, link. Those components are not meant to be used
   * directly.
   *
   * The href prop is optional, since some cards are clickable, which makes the underlying cta
   * a button element, without a link. Those without a preset href, have a hrefType prop.
   */
  import { computed, resolveComponent } from 'vue'

  import { useLinkType } from '../../composables/link-type'
  import type { ButtonBaseType, CtaHrefType, CtaSize } from '../../models/button'

  type ButtonBaseProps = {
    theme?: 'dark' | 'light'
    href?: string
    disabled?: boolean
    label?: string

    hrefType?: CtaHrefType // We don't set a default value, since if it's set we can't override it in the button
    size?: CtaSize
    type?: ButtonBaseType
  }

  const {
    theme = 'dark',
    label = '',
    disabled = false,
    size = 'default',
    type = 'filled',
    href = '',
    hrefType
  } = defineProps<ButtonBaseProps>()

  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const NuxtLink = resolveComponent('NuxtLink')

  /**
   * We define the hrefType based on the href prop.
   *
   * Depending on the href type, a different icon is rendered on the button. IF
   * a hrefType was already passed to the base component, we use that one.
   */
  const computedHrefType = computed(() => {
    if (hrefType) {
      return hrefType
    }

    if (href && href.length > 0) {
      return useLinkType(href)
    }

    return 'internal'
  })

  /**
   * Component to render
   *
   * In order to render NuxtLink as a dynamic component, we need to use
   * the resolveComponent function.
   */
  const componentToShow = computed(() => {
    if (href && href.length > 0) {
      return resolveComponent('NuxtLink')
    }
    return 'button'
  })

  /**
   * Capitalize Button type
   */
  const buttonType = computed(() => {
    return type.charAt(0).toUpperCase() + type.slice(1)
  })
</script>
